import { useState, useMemo, type ReactNode } from "react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  LinkBackwardIcon,
  Download04Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  AlertCircleIcon,
  LinkBackwardIcon as LinkBackwardSolidIcon
} from "@hugeicons-pro/core-solid-rounded";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { cx } from "shared/utils";
import { Show } from "shared/components";
import { EmailFrame } from "shared/components/EmailFrame";
import { MessageContentCard } from "./MessageContentCard";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { type Attachment, type ReplyTarget } from "../types";

const BODY_COLLAPSE_THRESHOLD = 300;
// The pop-up collapses to the design's 3-line text preview much earlier.
const COMPACT_COLLAPSE_THRESHOLD = 160;
// Collapsed HTML is clipped by height; slicing the markup would cut mid-tag.
const COLLAPSED_MAX_HEIGHT = 320;

/**
 * Replace cid: references in HTML with actual attachment URLs.
 * - If the attachment URL is available, replace cid: with the real URL.
 * - If the attachment exists but URL is empty (upload pending), show a
 *   loading placeholder so the image appears once the upload finishes.
 * - If no matching attachment exists (e.g. from quoted replies), remove
 *   the image tag entirely.
 */
const replaceInlineImages = (
  html: string,
  attachments: Attachment[]
): string => {
  const inlineMap = new Map<string, string>();
  for (const att of attachments)
    if (att.is_inline && att.content_id)
      inlineMap.set(att.content_id, att.file_url);

  return html.replace(/<img[^>]*src="cid:([^"]+)"[^>]*>/gi, (imgTag, cid) => {
    if (!inlineMap.has(cid))
      // No matching attachment at all — remove (e.g. from quoted replies)
      return "";

    const url = inlineMap.get(cid)!;
    if (url) return imgTag.replace(`src="cid:${cid}"`, `src="${url}"`);

    // Attachment exists but file is still uploading — show placeholder
    return `<div style="display:inline-block;background:#f0f0f0;border-radius:8px;padding:16px 24px;color:#888;font-size:12px;margin:4px 0;">Loading image...</div>`;
  });
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface AddressFieldProps {
  label: string;
  address: string;
}

const AddressField = ({ label, address }: AddressFieldProps) => (
  <div className="flex items-center gap-2">
    <span className="font-medium text-content-100">{label}</span>
    <span className="text-content-300">{address}</span>
  </div>
);

// Collapse long recipient lists behind a "+N" chip that expands on click.
const RECIPIENTS_VISIBLE_LIMIT = 3;

const AddressList = ({
  label,
  addresses
}: {
  label: string;
  addresses: string[];
}) => {
  const [expanded, setExpanded] = useState(false);

  if (addresses.length === 0) return null;

  const overflow = addresses.length - RECIPIENTS_VISIBLE_LIMIT;
  const visible =
    expanded || overflow <= 0
      ? addresses
      : addresses.slice(0, RECIPIENTS_VISIBLE_LIMIT);

  return (
    <div className="flex items-start gap-2">
      <span className="font-medium text-content-100 flex-shrink-0">
        {label}
      </span>
      <span className="text-content-300 break-words">
        {visible.join(", ")}
        {!expanded && overflow > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="ml-1 font-medium text-content-200 hover:text-content-1000"
            aria-label={`Show ${overflow} more recipient${overflow > 1 ? "s" : ""}`}
          >
            +{overflow}
          </button>
        ) : null}
      </span>
    </div>
  );
};

// Pop-up From/To rows (2152:17512): floating stacks name over address, the
// drawer keeps one "name • address" line capped at the design's 300px.
const CompactAddressRow = ({
  label,
  name,
  address,
  inline,
  overflowCount = 0,
  trailing
}: {
  label: string;
  name?: string;
  address?: string;
  inline: boolean;
  overflowCount?: number;
  trailing?: ReactNode;
}) => {
  if (!name && !address) return null;
  // A name that is just the address again adds nothing.
  const displayName = name !== address ? name : undefined;
  const suffix = overflowCount > 0 ? ` +${overflowCount}` : "";
  const labelNode = (
    <span className="w-8 shrink-0 font-medium text-content-100">{label}</span>
  );
  const nameNode = displayName ? (
    <span className={cx("truncate", inline ? "max-w-[300px]" : "")}>
      {displayName}
    </span>
  ) : null;

  if (inline)
    return (
      <div className="flex items-center gap-1">
        {labelNode}
        <span className="flex min-w-0 items-center gap-1 font-medium text-content-300">
          {nameNode}
          <Show if={Boolean(displayName && address)}>
            <span className="size-0.5 shrink-0 rounded-full bg-content-100" />
          </Show>
          <Show if={Boolean(address)}>
            <span className="max-w-[300px] truncate">
              {address}
              {suffix}
            </span>
          </Show>
          {trailing}
        </span>
      </div>
    );

  return (
    <div className="flex items-start gap-1">
      {labelNode}
      <span className="flex min-w-0 flex-col font-medium text-content-300">
        {nameNode}
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate">
            {address}
            {suffix}
          </span>
          {trailing}
        </span>
      </span>
    </div>
  );
};

const FromField = ({
  address,
  sentViaQuotaFallback
}: {
  address: string;
  sentViaQuotaFallback: boolean;
}) => (
  <div className="flex items-center gap-2">
    <AddressField label="From:" address={address} />
    {sentViaQuotaFallback ? <QuotaFallbackHint address={address} /> : null}
  </div>
);

const QuotaFallbackHint = ({ address }: { address: string }) => (
  <Tooltip placement="top">
    <TooltipTrigger as="div" className="cursor-pointer">
      <HugeiconsIcon
        icon={AlertCircleIcon}
        size={14}
        className="text-amber-500"
      />
    </TooltipTrigger>
    <TooltipContent className="max-w-[280px] rounded-lg border bg-alpha-800/90 backdrop-blur-sm px-3 py-2 text-xs text-content-1000-inverted shadow-xs">
      Sent from {address} because the connected mailbox reached its daily
      sending limit.
    </TooltipContent>
  </Tooltip>
);

// The card's From/To/Cc/Bcc header. The pop-up (compact) renders the design's
// 11px rows — stacked on the floating card, single "name • address" lines in
// the drawer; /inbox keeps its original block.
const EmailAddresses = ({
  compact,
  inline,
  from,
  to,
  toList,
  ccList,
  bccList,
  sentViaQuotaFallback
}: {
  compact: boolean;
  inline: boolean;
  from?: MessageAddressLite;
  to?: MessageAddressLite;
  toList: string[];
  ccList: string[];
  bccList: string[];
  sentViaQuotaFallback: boolean;
}) => {
  const hasAny =
    Boolean(from) ||
    toList.length > 0 ||
    ccList.length > 0 ||
    bccList.length > 0;
  if (!hasAny) return null;

  if (compact)
    return (
      <div className="space-y-1 !border-t-0 pb-3 text-[11px] leading-4">
        <CompactAddressRow
          label="From:"
          name={from?.name}
          address={from?.address}
          inline={inline}
          trailing={
            sentViaQuotaFallback && from ? (
              <QuotaFallbackHint address={from.address} />
            ) : null
          }
        />
        <CompactAddressRow
          label="To:"
          name={to?.name}
          address={toList[0] ?? to?.address}
          overflowCount={Math.max(toList.length - 1, 0)}
          inline={inline}
        />
        <AddressList label="Cc:" addresses={ccList} />
        <AddressList label="Bcc:" addresses={bccList} />
      </div>
    );

  return (
    <div className="space-y-2 text-xs py-4">
      {from ? (
        <FromField
          address={from.address}
          sentViaQuotaFallback={sentViaQuotaFallback}
        />
      ) : null}
      <AddressList label="To:" addresses={toList} />
      <AddressList label="Cc:" addresses={ccList} />
      <AddressList label="Bcc:" addresses={bccList} />
    </div>
  );
};

const downloadAttachment = (fileUrl: string, filename: string) => {
  const xhr = new XMLHttpRequest();
  xhr.open("GET", fileUrl, true);
  xhr.responseType = "blob";
  xhr.onload = () => {
    // eslint-disable-next-line compat/compat -- URL.createObjectURL is required for cross-origin downloads; supported in all target browsers
    const url = window.URL.createObjectURL(xhr.response);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    // eslint-disable-next-line compat/compat
    window.URL.revokeObjectURL(url);
  };
  xhr.send();
};

interface MessageAddressLite {
  name: string;
  address: string;
}

interface EmailTitleProps {
  subject?: string;
  isReply: boolean;
  body: string;
  messageId?: string;
  from?: MessageAddressLite;
  onReply?: (target: ReplyTarget) => void;
  compact?: boolean;
}

const EmailTitle = ({
  subject,
  isReply,
  body,
  messageId,
  from,
  onReply,
  compact = false
}: EmailTitleProps) => {
  const canReply = !!(messageId && onReply && from);
  return (
    <div
      className={cx(
        "flex items-center justify-between gap-3",
        compact ? "pb-2" : "pb-4"
      )}
    >
      <h3 className="flex items-center gap-2 text-base font-bold text-content-1000">
        {isReply ? (
          <HugeiconsIcon
            icon={LinkBackwardSolidIcon}
            size={20}
            className="flex-shrink-0 text-content-1000"
          />
        ) : null}
        {subject}
      </h3>

      {canReply ? (
        <IconButton
          HugeIcon={LinkBackwardIcon}
          variant={IconButtonVariant.BASE}
          size={compact ? IconButtonSize.XS : IconButtonSize.SM}
          onClick={() =>
            onReply!({
              messageId: messageId!,
              fromName: from!.name,
              fromAddress: from!.address,
              preview: (body || "").slice(0, 160)
            })
          }
          className="flex-shrink-0"
          aria-label="Reply"
        />
      ) : null}
    </div>
  );
};

interface AttachmentItemProps {
  attachment: Attachment;
}

const AttachmentItem = ({ attachment }: AttachmentItemProps) => (
  <a
    href={attachment.file_url}
    download={attachment.filename}
    onClick={(e) => {
      e.preventDefault();
      downloadAttachment(attachment.file_url, attachment.filename);
    }}
    className="flex items-center gap-3 p-3 rounded-xl border border-alpha-100 hover:bg-background-800 transition-colors"
  >
    <HugeiconsIcon
      icon={Download04Icon}
      size={24}
      className="text-content-300 flex-shrink-0"
    />
    <div className="flex flex-col min-w-0">
      <span className="text-sm font-medium text-content-1000 truncate">
        {attachment.filename}
      </span>
      <span className="text-xs text-content-200">
        File - {formatFileSize(attachment.file_size_bytes)}
      </span>
    </div>
  </a>
);

interface MessageAddress {
  name: string;
  address: string;
}

const EmailBody = ({
  html,
  plainText,
  subject,
  isCollapsed,
  compact = false,
  onCanvasWidth
}: {
  html: string | null;
  plainText: string;
  subject?: string;
  isCollapsed: boolean;
  /** Pop-up: a collapsed email shows the design's 3-line text preview. */
  compact?: boolean;
  onCanvasWidth: (width: number) => void;
}) => {
  const clampClass = compact && isCollapsed ? "line-clamp-3" : undefined;
  return (
    <div
      className={cx(
        "text-sm text-content-1000 break-words",
        compact ? "pt-3 pb-4" : "py-4"
      )}
    >
      {html && !(compact && isCollapsed) ? (
        <div
          className={cx(isCollapsed ? "overflow-hidden" : undefined)}
          style={isCollapsed ? { maxHeight: COLLAPSED_MAX_HEIGHT } : undefined}
        >
          <EmailFrame
            html={html}
            title={subject ?? "Email body"}
            onCanvasWidth={onCanvasWidth}
          />
        </div>
      ) : (
        <div className={clampClass}>{plainText}</div>
      )}
    </div>
  );
};

interface EmailContentProps {
  isFromContact: boolean;
  body: string;
  bodyHtml?: string;
  subject?: string;
  isReply?: boolean;
  sentViaQuotaFallback?: boolean;
  from?: MessageAddress;
  to?: MessageAddress;
  toAddresses?: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  messageId?: string;
  onReply?: (target: ReplyTarget) => void;
  attachments?: Attachment[];
  className?: string;
}

export const EmailContent = ({
  isFromContact,
  body,
  bodyHtml,
  subject,
  isReply = false,
  sentViaQuotaFallback = false,
  from,
  to,
  toAddresses,
  ccAddresses,
  bccAddresses,
  messageId,
  onReply,
  attachments,
  className
}: EmailContentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const isDrawerEmbed = embed?.layout === "drawer";

  // Fall back to the single `to` for messages synced before cc/bcc were stored.
  const toFallback = to ? [to.address] : [];
  const toList =
    toAddresses && toAddresses.length > 0 ? toAddresses : toFallback;
  const ccList = ccAddresses ?? [];
  const bccList = bccAddresses ?? [];

  const contentToMeasure = bodyHtml || body;
  const shouldCollapse =
    contentToMeasure.length >
    (isCompact ? COMPACT_COLLAPSE_THRESHOLD : BODY_COLLAPSE_THRESHOLD);

  // EmailFrame sanitizes; only the cid: rewrite needs the attachments.
  const preparedHtml = useMemo(() => {
    if (!bodyHtml) return null;
    return attachments?.length
      ? replaceInlineImages(bodyHtml, attachments)
      : bodyHtml;
  }, [bodyHtml, attachments]);

  const fileAttachments = useMemo(
    () => attachments?.filter((att) => !att.is_inline) ?? [],
    [attachments]
  );

  const [canvasWidth, setCanvasWidth] = useState<number>();

  const isCollapsed = shouldCollapse && !isExpanded;

  let plainBodyContent = body;
  // Compact collapse clamps with line-clamp-3 instead of slicing.
  if (isCollapsed && !isCompact)
    plainBodyContent = `${body.slice(0, BODY_COLLAPSE_THRESHOLD)}...`;

  return (
    <MessageContentCard
      isFromContact={isFromContact}
      contentWidth={canvasWidth}
      className={cx(
        "flex flex-col divide-y divide-alpha-40",
        // Pop-up email card (2152:17506): 20px gutters, 12px under the last row.
        isCompact ? "px-5 pb-3" : "",
        className
      )}
    >
      <EmailTitle
        subject={subject}
        isReply={isReply}
        body={body}
        messageId={messageId}
        from={from}
        onReply={onReply}
        compact={isCompact}
      />

      <EmailAddresses
        compact={isCompact}
        inline={isDrawerEmbed}
        from={from}
        to={to}
        toList={toList}
        ccList={ccList}
        bccList={bccList}
        sentViaQuotaFallback={sentViaQuotaFallback}
      />

      <EmailBody
        html={preparedHtml}
        plainText={plainBodyContent}
        subject={subject}
        isCollapsed={isCollapsed}
        compact={isCompact}
        onCanvasWidth={setCanvasWidth}
      />

      {fileAttachments.length > 0 && (
        <div className="grid grid-cols-2 gap-3 pt-4">
          {fileAttachments.map((attachment) => (
            <AttachmentItem key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}

      {shouldCollapse ? (
        <div
          className={cx("flex w-full justify-end", isCompact ? "pt-1" : "pt-4")}
        >
          <Button
            HugeIcon={isExpanded ? ArrowUp01Icon : ArrowDown01Icon}
            variant={ButtonVariant.TEXT}
            size={ButtonSize.SM}
            className="flex gap-1 items-center !text-content-200"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "Show less" : "Show more"}
          </Button>
        </div>
      ) : null}
    </MessageContentCard>
  );
};
